import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const project = snapshot => {
    const value = snapshot.data();
    return { uid: snapshot.id, name: typeof value.name === 'string' ? value.name : '',
        email: typeof value.email === 'string' ? value.email : '' };
};
// Strip formatting and the local/international prefix so 0133980171, 60133980171
// and +60 13-398 0171 all compare equal.
const phoneCore = value => String(value || '').replace(/\D/g, '').replace(/^(?:60|0)/, '');
// Read-only admin directory. Searches SESAAT users by name/email, and — when the
// term looks like a phone number — the WhatsApp number stored on flow_subs (what
// the customer table shows). No new search index.
export async function searchFlowCustomers(input) {
    const term = input.trim();
    if (term.length < 2 || term.length > 128) throw new Error('Enter 2–128 characters.');
    const users = new Map();
    const core = phoneCore(term);
    if (term.replace(/\D/g, '').length >= 4 && core) {
        // Flow Ultra buyers (small): partial WhatsApp match on flow_subs.
        const subs = await getDocs(collection(db, 'flow_subs'));
        subs.docs.forEach(d => { const v = d.data();
            if (v.uid && phoneCore(v.contactPhone).includes(core)) users.set(v.uid, { uid: v.uid, name: v.contactName || '', email: v.email || '', phone: v.contactPhone || '' }); });
        // Any SESAAT account (even without a Flow Ultra sub): exact WhatsApp match on
        // users.whatsappNumber (stored E.164 like +60…). Exact only, to stay cheap.
        const digits = term.replace(/\D/g, '');
        const variants = [...new Set(['+60' + core, '60' + core, '0' + core, '+' + digits, digits])].slice(0, 10);
        const byWa = await getDocs(query(collection(db, 'users'), where('whatsappNumber', 'in', variants), limit(25)));
        byWa.docs.forEach(d => { if (!users.has(d.id)) { const v = d.data();
            users.set(d.id, { uid: d.id, name: typeof v.name === 'string' ? v.name : '', email: typeof v.email === 'string' ? v.email : '', phone: v.whatsappNumber || '' }); } });
        // Manual Flow customers (flow_tenants, no uid): match the phone, then find
        // their SESAAT account by the email the admin saved on the tenant
        // (Flow Ultra popup → Email). Added 2026-09-28. Small collection, read admin-only.
        const tenants = await getDocs(collection(db, 'flow_tenants')).catch(() => ({ docs: [] }));
        const tEmails = {};
        tenants.docs.forEach(d => { const v = d.data(); const e = String(v.email || '').trim().toLowerCase();
            if (e && phoneCore(v.phone).includes(core)) tEmails[e] = v.phone || ''; });
        const emailList = Object.keys(tEmails).slice(0, 30);
        for (let i = 0; i < emailList.length; i += 10) {
            const byEmail = await getDocs(query(collection(db, 'users'), where('email', 'in', emailList.slice(i, i + 10)), limit(25)));
            byEmail.docs.forEach(d => { if (!users.has(d.id)) { const v = d.data(); const e = String(v.email || '').toLowerCase();
                users.set(d.id, { uid: d.id, name: typeof v.name === 'string' ? v.name : '', email: v.email || '', phone: tEmails[e] || v.whatsappNumber || '' }); } });
        }
    }
    const names = [...new Set([term, term.toLowerCase(), term.toUpperCase(), term.replace(/\b\w/g, c => c.toUpperCase())])];
    const searches = [...new Set([term, term.toLowerCase()])].map(value => ['email', value]);
    searches.push(...names.map(value => ['name', value]));
    const snapshots = await Promise.all(searches.map(([field, value]) => getDocs(query(collection(db, 'users'),
        where(field, '>=', value), where(field, '<=', value + '\uf8ff'), limit(25)))));
    snapshots.forEach(s => s.docs.forEach(d => { if (!users.has(d.id)) users.set(d.id, project(d)); }));
    // A subscription added by WhatsApp number only (Add customer, 3 Oct 2026) carries no
    // name or email, so the row used to show the raw account id. Fill both from the
    // customer's own SESAAT account. A failed read leaves the row as it was.
    await Promise.all([...users.values()].filter(u => !u.name && !u.email).slice(0, 30).map(async u => {
        const s = await getDoc(doc(db, 'users', u.uid)).catch(() => null);
        if (!s?.exists()) return;
        const v = project(s); u.name = v.name; u.email = v.email;
    }));
    return { users: [...users.values()].sort((a, b) => (a.email || a.name || a.uid).localeCompare(b.email || b.name || b.uid)),
        limited: snapshots.some(s => s.size === 25) };
}
export async function loadFlowCustomerNames(uids) {
    const values = [...new Set(uids)];
    if (values.length > 100 || values.some(uid => !/^[A-Za-z0-9_-]{1,128}$/.test(uid))) throw new Error('Invalid customer list.');
    const snapshots = await Promise.all(values.map(uid => getDoc(doc(db, 'users', uid))));
    return snapshots.filter(s => s.exists()).map(project);
}

// Enrich pilot customers with their real subscription (WhatsApp, expiry) so the
// admin does not retype it. Two sources:
//   • flow_subs   — Billplz payments, keyed by email, carry a `uid` (exact map).
//   • flow_tenants — manual "Add customer" entries with NO uid; matched to a uid
//                    by email or WhatsApp via the customer's SESAAT users doc.
// Billplz wins when both exist. Read admin-only, same as the Flow Ultra page.
const toMillis = v => (v && typeof v.toMillis === 'function' ? v.toMillis() : (v instanceof Date ? v.getTime() : null));
export async function loadFlowSubscriptions(uids) {
    const wanted = new Set(uids.filter(u => typeof u === 'string'));
    if (!wanted.size) return {};
    const map = {};
    const subsSnap = await getDocs(collection(db, 'flow_subs'));
    // Fasa 3A (3 Okt 2026): flow_subs kini memegang Billplz DAN manual (source:'manual'),
    // dan satu orang boleh ada lebih dari satu langganan (Type 1 + Type 2). Tetingkap
    // akses Session Access = langganan TYPE 1 (Type 2 ada akses Gmailnya sendiri);
    // beberapa calon → yang tamat paling lewat. Rekod dibatalkan dilangkau.
    const projek = v => ({
        phone: typeof v.contactPhone === 'string' ? v.contactPhone : '',
        name: typeof v.contactName === 'string' ? v.contactName : '',
        email: typeof v.email === 'string' ? v.email : '',
        expiresAt: toMillis(v.expiresAt),
        status: v.status || null, source: v.source === 'manual' ? 'manual' : 'billplz',
        jenis: v.jenis === 2 ? 2 : 1,
    });
    const lebihBaik = (baru, lama) => !lama || (baru.jenis === 1 && lama.jenis === 2)
        || (baru.jenis === lama.jenis && (baru.expiresAt || 0) > (lama.expiresAt || 0));
    const hidup = subsSnap.docs.map(d => d.data()).filter(v => v.status !== 'cancelled');
    hidup.forEach(v => {
        if (v.uid && wanted.has(v.uid)) { const c = projek(v); if (lebihBaik(c, map[v.uid])) map[v.uid] = c; }
    });
    const idSub = new Set(subsSnap.docs.map(d => d.id));
    // Manual entries: for uids with no Billplz sub, read their SESAAT contact and
    // match a flow_tenants row by email or WhatsApp, then take its expiry.
    // A uid whose only match so far is Type 2 is looked up too: an older Type 1 row may
    // still sit in flow_tenants, and `jenis` here decides whether Session Access may
    // assign the customer at all (Type 2 only → locked; see FlowSessionCustomerPicker).
    const missing = [...wanted].filter(uid => !map[uid] || map[uid].jenis === 2);
    if (missing.length) {
        const userSnaps = await Promise.all(missing.map(uid => getDoc(doc(db, 'users', uid))));
        const contact = {};
        userSnaps.forEach(s => { if (s.exists()) { const v = s.data();
            contact[s.id] = { email: (v.email || '').toLowerCase(), phone: phoneCore(v.whatsappNumber) }; } });
        // Langganan manual dalam flow_subs yang BELUM ada uid (dimasukkan sebelum pelanggan mendaftar).
        hidup.filter(v => !v.uid && v.source === 'manual').forEach(v => {
            const tEmail = (v.email || '').toLowerCase(), tPhone = phoneCore(v.contactPhone);
            for (const uid of missing) {
                const c = contact[uid];
                if (!c) continue;
                if ((tEmail && tEmail === c.email) || (tPhone && c.phone && tPhone === c.phone)) {
                    const x = projek(v); if (lebihBaik(x, map[uid])) map[uid] = x;
                }
            }
        });
        const tenantsSnap = await getDocs(collection(db, 'flow_tenants')).catch(() => ({ docs: [] }));
        tenantsSnap.docs.filter(d => !idSub.has('m_' + d.id) && !d.data().migratedTo).forEach(d => {   // yang sudah dipindah dilangkau
            const v = d.data();
            const tEmail = (v.email || '').toLowerCase(), tPhone = phoneCore(v.phone);
            const jenis = v.jenis === 2 ? 2 : 1;
            for (const uid of missing) {
                const c = contact[uid];
                // First match stays, except a Type 1 row replaces a Type 2 one (same rule as lebihBaik).
                if (!c || (map[uid] && !(map[uid].jenis === 2 && jenis === 1))) continue;
                if ((tEmail && tEmail === c.email) || (tPhone && c.phone && tPhone === c.phone)) {
                    map[uid] = { phone: v.phone || '', name: v.name || '', email: v.email || '',
                        expiresAt: toMillis(v.expiresAt), status: v.status || null, source: 'manual', jenis };
                }
            }
        });
    }
    return map;
}
